/**
 * ページ全体の環境光（メッシュグラデーション / オーロラ背景）。
 *
 * 大きくぼかした色の塊を数個置き、その上に細かいノイズを乗せる。
 * **ノイズが要点。** 暗い地に淡いグラデーションを敷くと必ず帯（バンディング）が出て
 * 安っぽくなる。3〜4%のグレインを重ねると帯が潰れて質感が出る。
 *
 * **絶対にアニメーションさせないこと。** 静止していれば初回に1回ラスタライズされるだけで、
 * 以降のコストはゼロ。動かした瞬間に大面積の再ラスタライズが毎フレーム走る
 * （バナーのWebGL歪みで実測170〜300%まで上がって撤去した件と同じ）。
 *
 * 色はアクセント色から色相をずらして導出する。固定色にしないのは
 * 「自分の色の環境光」にするため。
 *
 * 【まとまりを出すための条件】一度、色相を±96°振って4個置いたら
 * 「ごちゃごちゃで統一感がない」状態になった。効いたのは次の3つ。
 * - **色相の振り幅は±30°以内（同系色）に収める。** 広げるほど虹色のスープになる
 * - **中心を画面の外に置く。** 内側に置くと塊の中心が「点」として見える。
 *   外に出すと減衰の裾だけが見えるので、1枚の面としてつながる
 * - **半径を画面より大きく取り、数を減らす。** 重なって1つの流れになる
 */

import { hslOf, wrapHue } from '../utils/color';

export type AmbientKey = 'none' | 'soft' | 'aurora';

interface Blob {
  /** 中心位置（％） */
  x: number;
  y: number;
  /** 半径（％）。大きいほど広く薄く広がる */
  r: number;
  /** アクセントからの色相のずれ（度） */
  hue: number;
  alpha: number;
}

export interface AmbientPreset {
  key: AmbientKey;
  label: string;
  description: string;
  minPlan: number;
  blobs?: Blob[];
  /** 明度。暗い地に乗せるので上げすぎないこと */
  lightness?: number;
  saturation?: number;
  grain?: number;
}

export const AMBIENT_PRESETS: AmbientPreset[] = [
  { key: 'none', label: 'なし', description: '単色の地', minPlan: 0 },
  {
    key: 'soft',
    label: 'ソフト',
    description: '角にうっすら色が溜まる',
    minPlan: 0,
    lightness: 18,
    saturation: 40,
    grain: 0.03,
    blobs: [
      { x: -6, y: -8, r: 92, hue: 0, alpha: 0.42 },
      { x: 106, y: 62, r: 88, hue: 20, alpha: 0.3 },
    ],
  },
  {
    // 「オーロラ」と呼んでいたが、実装は静止した光の溜まりで、
    // 流れる光ではない。名前が実装を上回っていたので改名した。
    // 本物のオーロラ表現は動かす必要があり、大面積のアニメーションは採らない方針。
    key: 'aurora',
    label: 'グロウ',
    description: '色が濃く広がる',
    minPlan: 0,
    lightness: 21,
    saturation: 52,
    grain: 0.04,
    blobs: [
      { x: -4, y: -10, r: 100, hue: 0, alpha: 0.55 },
      { x: 108, y: 20, r: 95, hue: 26, alpha: 0.42 },
      { x: 58, y: 112, r: 105, hue: -28, alpha: 0.38 },
    ],
  },
];

export const DEFAULT_AMBIENT: AmbientKey = 'none';

export const canUseAmbient = (key: string, plan: number): boolean =>
  plan >= (AMBIENT_PRESETS.find(p => p.key === key)?.minPlan ?? 0);

export const resolveAmbient = (key: string | undefined, plan: number): AmbientKey => {
  const preset = AMBIENT_PRESETS.find(p => p.key === key);
  return preset && plan >= preset.minPlan ? preset.key : DEFAULT_AMBIENT;
};

export const ambientPreset = (key: string): AmbientPreset | undefined =>
  AMBIENT_PRESETS.find(p => p.key === key && p.blobs);

/** 色の塊を重ねた background 文字列。彩度・明度はプリセット側で決める */
export const ambientBackground = (preset: AmbientPreset, accent: string): string => {
  const base = hslOf(accent).h;
  const color = (hueShift: number, alpha: number) =>
    `hsl(${wrapHue(base + hueShift)} ${preset.saturation}% ${preset.lightness}% / ${alpha})`;
  return (preset.blobs ?? [])
    .map(b =>
      // 減衰は3点で作る。2点だと落ち方が直線的で輪郭が見えてしまう
      `radial-gradient(${b.r}% ${b.r}% at ${b.x}% ${b.y}%, `
      + `${color(b.hue, b.alpha)} 0%, `
      + `${color(b.hue, b.alpha * 0.32)} 42%, `
      + `${color(b.hue, 0)} 78%)`
    )
    .join(', ');
};

/**
 * グレイン。SVG の feTurbulence を data URI で焼き込む。
 * 静止画なのでラスタライズは初回1回だけ。
 * stitchTiles を付けないとタイルの継ぎ目が線として見える。
 */
export const GRAIN_URL =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23n)'/%3E%3C/svg%3E\")";
